import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  People as PeopleIcon,
  PhoneCallback as PhoneCallbackIcon,
  Description as DescriptionIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAgentReport } from '../../../contexts/AgentReportContext';
import { useLoader } from '../../../contexts/LoaderContext';

// Sample data matching the image
const sampleData = [
  {
    label: 'Total Leads',
    value: 1250,
    percentage: 100,
    color: '#5C8FFB',
    icon: PersonIcon,
  },
  {
    label: 'Lead Contacted',
    value: 850,
    percentage: 68,
    color: '#9B6EFB',
    icon: PeopleIcon,
  },
  {
    label: 'Lead Connected',
    value: 720,
    percentage: 58,
    color: '#7B8FFB',
    icon: PhoneCallbackIcon,
  },
  {
    label: 'Details Shared',
    value: 520,
    percentage: 42,
    color: '#EB578F',
    icon: DescriptionIcon,
  },
  {
    label: 'Visit Scheduled',
    value: 320,
    percentage: 26,
    color: '#F7A64B',
    icon: ScheduleIcon,
  },
  {
    label: 'Visit Completed',
    value: 240,
    percentage: 19,
    color: '#50C88F',
    icon: CheckCircleIcon,
  },
  {
    label: 'Bookings Closed',
    value: 85,
    percentage: 7,
    color: '#4BC8A6',
    icon: AttachMoneyIcon,
  },
];

const defaultData = sampleData;

const SalesFunnelOverview = ({ dateRangeState, periodType }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { fetchAgentSalesFunnelAnalytics, loading } = useAgentReport();
  const [funnelData, setFunnelData] = useState(defaultData);
  const { showLoader, hideLoader } = useLoader();

  // Map funnel stage labels to stat_type for leads navigation
  const getStatTypeForStage = (label) => {
    const mapping = {
      'Total Leads': 'total-leads',
      'Lead Contacted': 'contacted-leads',
      'Lead Connected': 'connected-leads',
      'Details Shared': 'details-shared',
      'Visit Scheduled': 'site-visit-scheduled',
      'Visit Completed': 'site-visit-done',
      'Bookings Closed': 'bookings-closed',
    };
    return mapping[label] || 'total-leads';
  };

  const handleStageClick = (stage) => {
    if (!dateRangeState?.currentStart || !dateRangeState?.currentEnd) return;
    
    const statType = getStatTypeForStage(stage.label);
    const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
    const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
    
    // Navigate to leads page with stat_type and date range
    navigate(`/leads?stat_type=${statType}&start_date=${startDate}&end_date=${endDate}`);
  };

  useEffect(() => {
    fetchData();
  }, [dateRangeState, periodType]);

  const fetchData = async () => {
    showLoader();
    try {
      const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
      const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
      const data = await fetchAgentSalesFunnelAnalytics(startDate, endDate, periodType);
      
      // Map API data to frontend format with icons and colors
      const iconMap = {
        'Total Leads': PersonIcon,
        'Lead Contacted': PeopleIcon,
        'Lead Connected': PhoneCallbackIcon,
        'Details Shared': DescriptionIcon,
        'Visit Scheduled': ScheduleIcon,
        'Visit Completed': CheckCircleIcon,
        'Bookings Closed': AttachMoneyIcon,
      };
      
      const colorMap = {
        'Total Leads': '#5C8FFB',
        'Lead Contacted': '#9B6EFB',
        'Lead Connected': '#7B8FFB',
        'Details Shared': '#EB578F',
        'Visit Scheduled': '#F7A64B',
        'Visit Completed': '#50C88F',
        'Bookings Closed': '#4BC8A6',
      };
      
      const formattedData = Array.isArray(data) ? data.map(item => ({
        label: item.label,
        value: item.value || 0,
        percentage: item.percentage || 0,
        color: colorMap[item.label] || '#5C8FFB',
        icon: iconMap[item.label] || PersonIcon,
      })) : sampleData;
      
      setFunnelData(formattedData);
    } catch (error) {
      console.error('Error fetching sales funnel data:', error);
      setFunnelData(sampleData);
    } finally {
      hideLoader();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        background: 'background.paper',
        height: '100%',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            color: 'text.primary',
            mb: 2,
          }}
        >
          Sales Funnel Metrics
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {funnelData.map((stage, index) => {
          const IconComponent = stage.icon;
          const fadedColor = alpha(stage.color, 0.15);

          return (
            <Box 
              key={index} 
              onClick={() => handleStageClick(stage)}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                cursor: 'pointer',
                p: 1,
                borderRadius: 1,
                transition: 'background-color 0.2s',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 32,
                  height: 32,
                }}
              >
                <IconComponent
                  sx={{
                    color: stage.color,
                    fontSize: '1.5rem',
                  }}
                />
              </Box>

              {/* Label and Progress Bar Container */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Label and Values Row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: 'text.primary',
                      fontSize: '0.875rem',
                    }}
                  >
                    {stage.label}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                      }}
                    >
                      {stage.percentage}%
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'bold',
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        minWidth: 50,
                        textAlign: 'right',
                      }}
                    >
                      {stage.value.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>

                {/* Progress Bar */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: fadedColor,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${stage.percentage}%`,
                      backgroundColor: stage.color,
                      borderRadius: 6,
                      transition: 'width 0.5s ease-in-out',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default SalesFunnelOverview;
